import React, { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';

interface PdfDownloadButtonProps {
  /**
   * The ID of the HTML element to convert to PDF
   */
  contentId: string;

  /**
   * The filename for the downloaded PDF (without .pdf extension)
   * @default "document"
   */
  filename?: string;

  /**
   * ARIA label for accessibility
   * @default "Download the content as a PDF document"
   */
  ariaLabel?: string;

  /**
   * Button variant from the UI library
   * @default "default"
   */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'hero';

  /**
   * Button size
   * @default "default"
   */
  size?: 'default' | 'sm' | 'lg' | 'icon';

  /**
   * Custom className for additional styling
   */
  className?: string;

  /**
   * Show download icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Button text
   * @default "Download as PDF"
   */
  children?: React.ReactNode;
}

/**
 * A React component that provides PDF download functionality for HTML content.
 * Uses jsPDF to convert HTML elements to PDF format.
 *
 * @example
 * ```tsx
 * <PdfDownloadButton
 *   contentId="report-content"
 *   filename="my-report"
 *   ariaLabel="Download the report as PDF"
 * />
 * ```
 */
export const PdfDownloadButton: React.FC<PdfDownloadButtonProps> = ({
  contentId,
  filename = 'document',
  ariaLabel = 'Download the content as a PDF document',
  variant = 'default',
  size = 'default',
  className,
  showIcon = true,
  children = 'Download as PDF',
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Handles the PDF generation and download process
   */
  const handleDownloadPdf = useCallback(async () => {
    try {
      setIsGenerating(true);

      // Get the content element to convert
      const contentElement = document.getElementById(contentId);

      if (!contentElement) {
        throw new Error(`Element with ID "${contentId}" not found`);
      }

      // Dynamically import jsPDF only when needed (code splitting)
      const { jsPDF } = await import('jspdf');

      // Create new PDF document with A4 portrait format
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      });

      // Convert HTML to PDF with proper positioning
      await doc.html(contentElement, {
        callback: (doc) => {
          // Save the PDF with specified filename
          doc.save(`${filename}.pdf`);
        },
        x: 20,
        y: 20,
        width: 555, // A4 width minus margins (595 - 40)
        windowWidth: 800, // Standard content width
      });

    } catch (error) {
      // Handle errors gracefully and log to console
      console.error('PDF generation error:', error);

      // Show user-friendly error message
      if (error instanceof Error) {
        alert(`Unable to generate PDF: ${error.message}`);
      } else {
        alert('Unable to generate PDF. Please check the console for details.');
      }
    } finally {
      setIsGenerating(false);
    }
  }, [contentId, filename]);

  return (
    <Button
      onClick={handleDownloadPdf}
      disabled={isGenerating}
      aria-label={ariaLabel}
      variant={variant}
      size={size}
      className={className}
      style={{ minHeight: '44px', minWidth: '44px' }} // Ensure minimum touch target
    >
      {showIcon && <Download className="h-4 w-4" />}
      {isGenerating ? 'Generating PDF...' : children}
    </Button>
  );
};

export default PdfDownloadButton;
